import { useEffect, useState } from "react";
import { BasketAPI } from "../../api/basketApi";
import { Button } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2 } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useAuthEvent } from "../../hooks/useAuthEvent";

interface CartItem {
  id: number;
  productId: string;
  count: number;
  pricePerUnit: number;
  name: string;
  brand: string;
  code: string;
  maxAvailable: number;
}

const Cart = () => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [updatingItems, setUpdatingItems] = useState<Set<number>>(new Set());
  const navigate = useNavigate();
  const { isRefreshingToken, isLoading } = useAuth();

  // Подписываемся на события авторизации
  useAuthEvent((user) => {
    console.log('User authenticated, refreshing cart data...');
    setRefreshKey(prev => prev + 1);
  });

  useEffect(() => {
    // Ждем завершения инициализации и обновления токена перед загрузкой корзины
    if (!isLoading && !isRefreshingToken) {
      fetchItems();
    }
  }, [refreshKey, isRefreshingToken, isLoading]);

  const fetchItems = async () => {
    try {
      const basketItems = await BasketAPI.getItems();
      setItems(basketItems);
    } catch (error) {
      console.error("Ошибка при загрузке корзины:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (itemId: number) => {
    try {
      await BasketAPI.deleteItem(itemId);
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error("Ошибка при удалении товара из корзины:", error);
    }
  };

  const handleQuantityChange = async (itemId: number, newCount: number) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;

    // Проверяем ограничения
    if (newCount < 1 || newCount > item.maxAvailable) return;

    setUpdatingItems(prev => new Set(prev).add(itemId));
    
    try {
      await BasketAPI.updateItem(itemId, newCount);
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error("Ошибка при изменении количества:", error);
    } finally {
      setUpdatingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  const totalAmount = items.reduce((sum, item) => {
    if (!item.pricePerUnit || !item.count) return sum;
    return sum + item.pricePerUnit * item.count;
  }, 0);
  
  const itemsCount = items.reduce((sum, item) => {
    if (!item.count) return sum;
    return sum + item.count;
  }, 0);

  if (loading) {
    return <div className="text-gray-300 mt-12">Загружаем информацию...</div>;
  }

  return (
    <div className="w-full max-w-7xl mx-auto px-4 py-6 md:py-10">
      <div className="flex-1">
        <h1 className="text-xl md:text-2xl font-bold mb-4 md:mb-6">Корзина</h1>
        {items.length === 0 ? (
          <div className="text-gray-500 text-sm md:text-base">Корзина пуста</div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1">
              {/* Десктопная таблица */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Товар</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Количество</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Цена за ед.</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Сумма</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {items.map((item, index) => (
                      <tr key={`${item.productId}-${index}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col">
                            <div className="text-sm font-medium text-gray-900">{item.name}</div>
                            <div className="text-sm text-gray-500">{item.brand} - {item.code}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleQuantityChange(item.id, item.count - 1)}
                              disabled={item.count <= 1 || updatingItems.has(item.id)}
                              className="p-1 rounded-full hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="text-sm font-medium min-w-[2rem] text-center">
                              {updatingItems.has(item.id) ? '' : item.count}
                            </span>
                            <button
                              onClick={() => handleQuantityChange(item.id, item.count + 1)}
                              disabled={item.count >= item.maxAvailable || updatingItems.has(item.id)}
                              className="p-1 rounded-full hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            Доступно: {item.maxAvailable}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.pricePerUnit.toFixed(2)} ₽
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {(item.pricePerUnit * item.count).toFixed(2)} ₽
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleRemove(item.id)}
                            disabled={updatingItems.has(item.id)}
                            className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Мобильные карточки */}
              <div className="md:hidden space-y-4">
                {items.map((item, index) => (
                  <div key={`${item.productId}-${index}`} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900 mb-1">{item.name}</div>
                        <div className="text-xs text-gray-500">{item.brand} - {item.code}</div>
                      </div>
                      <button
                        onClick={() => handleRemove(item.id)}
                        disabled={updatingItems.has(item.id)}
                        className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed ml-2"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-500">Цена за ед.:</span>
                      <span className="text-sm font-medium text-gray-900">{item.pricePerUnit.toFixed(2)} ₽</span>
                    </div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs text-gray-500">Количество:</span>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleQuantityChange(item.id, item.count - 1)}
                          disabled={item.count <= 1 || updatingItems.has(item.id)}
                          className="p-1 rounded-full hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="text-sm font-medium min-w-[2rem] text-center">
                          {updatingItems.has(item.id) ? '...' : item.count}
                        </span>
                        <button
                          onClick={() => handleQuantityChange(item.id, item.count + 1)}
                          disabled={item.count >= item.maxAvailable || updatingItems.has(item.id)}
                          className="p-1 rounded-full hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t">
                      <span className="text-xs text-gray-500">Доступно: {item.maxAvailable}</span>
                      <span className="text-base font-semibold text-gray-900">{(item.pricePerUnit * item.count).toFixed(2)} ₽</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="w-full lg:w-80 flex-shrink-0 order-first lg:order-last">
              <div className="bg-gray-50 rounded-lg p-4 md:p-6 lg:sticky lg:top-20">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-gray-500 text-sm md:text-base">Общая сумма:</span>
                  <span className="text-lg md:text-xl font-bold">{totalAmount.toFixed(2)} ₽</span>
                </div>
                <Button
                  className="w-full flex justify-center py-2.5 md:py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  onClick={() => navigate('/checkout')}
                  disabled={itemsCount === 0}
                >
                  Перейти к оформлению
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;